import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { useI18n, tr } from "@/lib/i18n";

export default function ProductCard({ product }) {
  const { lang, t } = useI18n();
  const detailPath = `/prodotti/${product.line}/${product.slug}`;
  const lineAccent = product.line === "pet" ? "pet" : "human";
  const dotColor = product.line === "pet" ? "var(--pet)" : "var(--human)";

  return (
    <article
      data-testid={`product-card-${product.slug}`}
      className="group bg-white border border-soft flex flex-col"
    >
      <Link
        to={detailPath}
        className="image-hover-scale relative aspect-[4/5] bg-cream-warm block overflow-hidden"
        aria-label={product.name}
      >
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-editorial px-2.5 py-1 bg-white/90 backdrop-blur-sm"
          >
            <span className="h-1.5 w-1.5 rounded-full" style={{ background: dotColor }} />
            {product.line === "pet" ? t("nav.pet") : t("nav.human")}
          </span>
        </div>
      </Link>

      <div className="p-6 flex-1 flex flex-col">
        <div className="text-xs uppercase tracking-editorial text-muted-ink mb-2">
          {tr(product, lang, "category") || product.category}
        </div>
        <h3 className="font-display text-2xl text-brand leading-tight">
          {tr(product, lang, "name") || product.name}
        </h3>
        <p className="mt-3 text-sm text-muted-ink leading-relaxed flex-1">
          {tr(product, lang, "short_description")}
        </p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {(tr(product, lang, "badges") || product.badges || []).slice(0, 3).map((b) => (
            <span
              key={b}
              className="text-[11px] px-2 py-1 border border-soft text-muted-ink"
            >
              {b}
            </span>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Link
            to={detailPath}
            className="inline-flex items-center gap-1 text-sm text-brand link-underline"
            data-testid={`product-card-${product.slug}-discover`}
          >
            {t("common.discover")} <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
          {product.amazon_url && (
            <a
              href={product.amazon_url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-brand text-white text-xs px-4 py-2 hover:bg-[#0f2020] transition-colors"
              data-testid={`product-card-${product.slug}-amazon`}
            >
              {t("common.buy_amazon")} <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
